/**
 * PremiumUpsellModal — Variant B (Challenger)
 *
 * Key differences from Variant A (control):
 * 1. Benefit-led headline instead of generic "Opgrader til Premium"
 * 2. Single punchy hook sentence before the feature table
 * 3. Price shown to guests as well as logged-in users
 * 4. CTA button text simplified to "Få Premium — 119 DKK"
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  PREMIUM_GUEST_CONTINUE_CTA_LABEL,
  getUpsellHeadline,
  getUpsellHook,
} from '../../constants/pricing';
import { FEATURE_UPSELL_LABELS } from '../../constants/pricingFeatures';
import { usePremiumUpgrade } from '../../hooks/usePremiumUpgrade';
import { resolveUserTier, trackPremiumCtaClick, trackPricingView } from '../../utils/analytics';
import { trackSelectPromotionAB, trackViewPromotionAB } from '../../utils/analyticsAB';
import { withNextQuery, withUpgradeIntent } from '../../utils/safeNextPath';
import PricingFeaturesComparison from '../PricingFeaturesComparison/PricingFeaturesComparison';
import CheckoutErrorNotice from '../CheckoutErrorNotice/CheckoutErrorNotice';
import './PremiumUpsellModal.css';
import './PremiumUpsellModalB.css';

const PremiumUpsellModalB = ({
  isOpen,
  onClose,
  feature = null,
  user = null,
  institutionSlug = '',
  placement = 'upsell_modal_b',
}) => {
  const navigate = useNavigate();
  const isAuthenticated = user !== null;
  const isPremium = user?.is_premium_active === true;
  const userTier = resolveUserTier(user);
  const VARIANT = 'B';
  const returnPath = withUpgradeIntent(window.location.pathname, feature);
  const source = 'upsell_modal_b';

  const { startCheckout, checkoutError, isStartingCheckout } = usePremiumUpgrade({
    returnPath,
    onBeforeRedirect: onClose,
    source,
    feature,
    institutionSlug,
    placement,
  });

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      trackViewPromotionAB(feature, userTier, VARIANT);
      trackPricingView({ feature, userTier, placement: 'upsell_modal_b' });
    }
  }, [isOpen, feature, userTier]);

  if (!isOpen) return null;

  const handleRegister = () => {
    trackSelectPromotionAB('register', feature, VARIANT);
    trackPremiumCtaClick({
      feature,
      cta: 'register',
      userTier,
      institutionSlug,
      placement: 'upsell_modal_b',
      source,
    });
    onClose();
    navigate(withNextQuery('/register', returnPath));
  };

  const handleLogin = () => {
    trackSelectPromotionAB('login', feature, VARIANT);
    onClose();
    navigate(withNextQuery('/login', returnPath));
  };

  const handleUpgradeClick = () => {
    trackSelectPromotionAB('upgrade', feature, VARIANT);
    startCheckout();
  };

  const featureLabel = feature && FEATURE_UPSELL_LABELS[feature]
    ? FEATURE_UPSELL_LABELS[feature]
    : null;

  const modal = (
    <div
      className="premium-upsell-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="premium-upsell-modal premium-upsell-modal--variant-b"
        onClick={(e) => e.stopPropagation()}
        role="document"
      >
        <button
          className="premium-upsell-modal-close"
          onClick={onClose}
          aria-label="Luk"
        >
          ×
        </button>

        <div className="premium-upsell-modal-content">

          <h2 className="premium-upsell-modal-title">
            {getUpsellHeadline({ isAuthenticated, feature })}
          </h2>

          <p className="premium-upsell-modal-hook">
            {getUpsellHook(feature)}
          </p>

          <div className="premium-upsell-modal-price premium-upsell-modal-price--variant-b">
            <div className="premium-price-b-row">
              <span className="premium-price-amount">119 DKK</span>
            </div>
            <p className="premium-price-period-b">Engangsbetaling · 1 års adgang · Intet abonnement</p>
          </div>

          <div className="premium-upsell-modal-features">
            <h3>Basis vs Premium</h3>
            <PricingFeaturesComparison variant="modal" />
          </div>

          {featureLabel && (
            <div className="premium-upsell-modal-feature-note">
              <p>
                Denne funktion kræver <strong>Premium</strong>:{' '}
                <strong>{featureLabel}</strong>
              </p>
            </div>
          )}

          <div className="premium-upsell-modal-actions">
            <CheckoutErrorNotice
              message={checkoutError}
              onRetry={!isAuthenticated ? undefined : handleUpgradeClick}
            />
            {!isAuthenticated ? (
              <>
                <button
                  className="premium-upsell-button premium-upsell-button-primary"
                  onClick={handleRegister}
                >
                  {PREMIUM_GUEST_CONTINUE_CTA_LABEL}
                </button>
                <button
                  className="premium-upsell-button premium-upsell-button-secondary"
                  onClick={handleLogin}
                >
                  Log ind
                </button>
              </>
            ) : !isPremium ? (
              <button
                className="premium-upsell-button premium-upsell-button-primary premium-upsell-button--variant-b"
                onClick={handleUpgradeClick}
                disabled={isStartingCheckout}
              >
                {isStartingCheckout ? 'Åbner betaling...' : 'Få Premium — 119 DKK'}
              </button>
            ) : null}
          </div>

        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default PremiumUpsellModalB;
